import axios from 'axios';

import * as Constants from '../../constants';
import { getConfig } from '../../reducers/portalConfig';
import { getValidationErrors } from '../../utils';
import { debouncedAxiosGet, stringifyUrl } from '../../utils/actions';
import { AXIOS_DEFAULT_OPTIONS } from '..';
import { upperCase, flatten } from 'lodash';

const init = (dispatch, isLoading = true) =>
  dispatch({
    type: Constants.APP_LOADING,
    isLoading,
  });

export const resetApp = () => dispatch => dispatch({ type: Constants.APP_RESET });

export const showLoading = isLoading => dispatch => init(dispatch, isLoading);

export const fetchRequestDetails = (requestUuid) =>
  async (dispatch, getState) => {
    init(dispatch);
    const config = getConfig(getState());
    const { portal } = config;

    const url = `${portal.hostname}/api/${portal.tenantPrefix}/api-management/0.1/application-requests/${requestUuid}`;
    await axios.get(url, AXIOS_DEFAULT_OPTIONS)
      .then(response =>
        dispatch({
          type: Constants.REQUEST_GET_DETAILS_SUCCESS,
          payload: response.data,
        }),
      )
      .catch(error =>
        dispatch({
          type: Constants.REQUEST_GET_DETAILS_ERROR,
          payload: getValidationErrors(error),
        }),
      );
  };

  export const fetchOrgSuggestions = ({ name, status }) =>
   (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = stringifyUrl({
      url: `${portal.hostname}/api/${portal.tenantPrefix}/tenant-admin/internal/organizations`,
      query: {
        name,
        ...(status ? { status } : {}),
      },
      options: {
        encode: true,
        skipNull: true,
        arrayFormat: 'comma',
      },
    });
    debouncedAxiosGet.cancel();
    debouncedAxiosGet(
      url,
      AXIOS_DEFAULT_OPTIONS,
      (response) => dispatch({
        type: Constants.REQUESTS_GET_ORG_SUGGEST_SUCCESS,
        payload: response.data,
      }),
      (error) => dispatch({
        type: Constants.REQUESTS_GET_ORG_SUGGEST_ERROR,
        payload: getValidationErrors(error),
      }),
    );
  };


  export const fetchListDetails = ({
    filterByName = '',
    filterByOrg = '',
    filterByType = 'ALL',
    page = 0,
    requestStatus = 'PENDING',
    entityUuid,
    size = 20,
  } = {}) =>
    async (dispatch, getState) => {
      init(dispatch);
      const config = getConfig(getState());
      const { portal } = config;


      const urlPrefix = `${portal.hostname}/api/${portal.tenantPrefix}/api-management/1.0/requests`;

      const getCascadingRequests = (totalPages) => {
        const cascadingRequests = [];
        for (let page = 1; page < totalPages; page++) {
          const url = stringifyUrl({
            url: urlPrefix,
            query: {
              ...(filterByOrg ? { organizationName: filterByOrg } : {}),
              ...(filterByName ? { entityName: filterByName } : {}),
              page,
              requestStatus,
              ...(filterByType === 'ALL' ? {} : { requestType: filterByType }),
              entityUuid,
              size,
            },
            options: {
              encode: true,
              skipNull: true,
              arrayFormat: 'comma',
            },
          });
          cascadingRequests.push(
            axios.get(url, AXIOS_DEFAULT_OPTIONS),
          );
        }
        return cascadingRequests;
      };

      const url = stringifyUrl({
        url: `${portal.hostname}/api/${portal.tenantPrefix}/api-management/1.0/requests`,
        query: {
          ...(filterByOrg ? { organizationName: filterByOrg } : {}),
          ...(filterByName ? { entityName: filterByName } : {}),
          page,
          requestStatus,
          ...(filterByType === 'ALL' ? {} : { requestType: filterByType }),
          entityUuid,
          size,
        },
        options: {
          encode: true,
          skipNull: true,
          arrayFormat: 'comma',
        },
      });

      await axios.get(url, AXIOS_DEFAULT_OPTIONS)
      .then(async response => {
        const { totalPages } = response.data;
        if (totalPages > 1) {
          const cascadingResponses = await Promise.all(
            getCascadingRequests(totalPages),
          );
          const cascadingResponsesResults = cascadingResponses
            .map(res => res.data.results);
          return {
            ...response.data,
            results: [
              ...response.data.results,
              ...flatten(cascadingResponsesResults),
            ],
          };
        }
        return response.data;
      })
      .then(response =>
        dispatch({
          type: Constants.REQUESTS_GET_LIST_DETAILS_SUCCESS,
          payload: response,
        }),
      )
      .catch(error =>
        dispatch({
          type: Constants.REQUESTS_GET_LIST_DETAILS_ERROR,
          payload: getValidationErrors(error),
        }),
      );
  };


export const fetchList = ({
  filterByEntityField = '',
  filterByEntityFieldType= Constants.REQUEST_FILTER_CONTAINS,
  filterByOrg = '',
  filterByType = 'ALL',
  filterByStartDate = '',
  filterByEndDate = '',
  page = 0,
  requestStatus = 'PENDING',
  entityUuid,
  size = 20,
} = {}) =>
  async (dispatch, getState) => {
    init(dispatch);
    const config = getConfig(getState());
    const { portal } = config;

    const entityNameParamKey = filterByEntityFieldType === Constants.REQUEST_FILTER_CONTAINS ? 'entityName' : 'entityNameNot'
    const url = stringifyUrl({
      url: `${portal.hostname}/api/${portal.tenantPrefix}/api-management/1.0/requests`,
      query: {
        ...(filterByOrg ? { organizationName: filterByOrg } : {}),
        ...(filterByEntityField ? { [`${entityNameParamKey}`]: filterByEntityField } : {}),
        ...(filterByStartDate ? { start : filterByStartDate } : {}),
        ...(filterByEndDate ? { end : filterByEndDate } : {}),
        page,
        requestStatus,
        ...(filterByType === 'ALL' ? {} : { requestType: filterByType }),
        entityUuid,
        size,
      },
      options: {
        encode: true,
        skipNull: true,
        arrayFormat: 'comma',
      },
    });

    await axios.get(url, AXIOS_DEFAULT_OPTIONS)
      .then(response =>
        dispatch({
          type: Constants.REQUESTS_GET_LIST_SUCCESS,
          payload: response.data,
        }),
      )
      .catch(error =>
        dispatch({
          type: Constants.REQUESTS_GET_LIST_ERROR,
          payload: getValidationErrors(error),
        }),
      );
  };

export const submitRequest = ({ isAccept, requestId, reason, requestType, assignedOrg,
  assignedRole, userEmail, shouldNotifyUser }) =>
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = stringifyUrl({
      url: `${portal.hostname}/api/${portal.tenantPrefix}/api-management/1.0/requests/${requestId}/reviews`,
      options: {
        encode: true,
        skipNull: true,
        arrayFormat: 'comma',
      },
    });
  const data = {
    uuid: requestId,
    action: isAccept ? 'ACCEPT' : 'REJECT',
    reason,
    requestType,
    sendResponseEmail : shouldNotifyUser,
    ...(assignedOrg ? { organizationUuid: assignedOrg } : {}),
    ...(assignedRole ? { roleUuid: assignedRole } : {}),
    ...(userEmail ? { email: userEmail } : {}),
  };
  dispatch({ type: Constants.REQUESTS_PUT_SUBMIT_REQUEST })
  await axios.put(url, data , AXIOS_DEFAULT_OPTIONS)
    .then(response =>
      dispatch({
        type: Constants.REQUESTS_PUT_SUBMIT_SUCCESS,
        payload: response.data,
      }),
    )
    .catch(error =>
      dispatch({
        type: Constants.REQUESTS_PUT_SUBMIT_ERROR,
        payload: getValidationErrors(error),
      }),
    );
};

export const submitBulkRegistrationRequests = (payload) =>
  async (dispatch, getState) => {
    const { isAccept, requestIds, reason, assignedOrg, assignedRole, shouldNotifyUser } = payload;
    const config = getConfig(getState());
    const { portal } = config;
    const url = stringifyUrl({
      url: `${portal.hostname}/api/${portal.tenantPrefix}/api-management/1.0/requests/registrations/reviews`,
      options: {
        encode: true,
        skipNull: true,
        arrayFormat: 'comma',
      },
    });
    const data = {
      registrationUuids: requestIds,
      action: isAccept ? 'ACCEPT' : 'REJECT',
      reason,
      sendResponseEmail : shouldNotifyUser,
      ...(assignedOrg && assignedRole ? {
        approvalOverrideValues: {
          organizationUuid: assignedOrg,
          roleUuid: assignedRole,
        },
      } : {}),
    };
    init(dispatch);
    dispatch({ type: Constants.REQUESTS_PUT_BULK_SUBMIT_REQUEST })
    await axios.put(url, data , AXIOS_DEFAULT_OPTIONS)
      .then(response =>
        dispatch({
          type: Constants.REQUESTS_PUT_BULK_SUBMIT_SUCCESS,
          payload: response.data,
        }),
      )
      .catch(error =>
        dispatch({
          type: Constants.REQUESTS_PUT_BULK_SUBMIT_ERROR,
          payload: error.response.data,
        }),
      );
  };

export const reviewApplication = (uuid, action, reason, requestType) =>
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = stringifyUrl({
      url: `${portal.hostname}/api/${portal.tenantPrefix}/api-management/1.0/requests/applications/${uuid}/reviews`,
      options: {
        encode: true,
        skipNull: true,
        arrayFormat: 'comma',
      },
    });
  const data = {
    uuid,
    action: upperCase(action),
    reason,
    requestType,
  };
  dispatch({ type: Constants.APPLICATION_REVIEWS_PUT_REQUEST })
  await axios.put(url, data , AXIOS_DEFAULT_OPTIONS)
    .then(response =>
      dispatch({
        type: Constants.APPLICATION_REVIEWS_PUT_SUCCESS,
        payload: response.data,
      }),
    )
    .catch(error =>
      dispatch({
        type: Constants.APPLICATION_REVIEWS_PUT_ERROR,
        payload: getValidationErrors(error),
      }),
    );
};

export const withdrawRequest = (uuid) =>
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/api/${portal.tenantPrefix}/api-management/1.0/requests/${uuid}/withdraw`;
  const data = {};
  init(dispatch);
  await axios.post(url, data , AXIOS_DEFAULT_OPTIONS)
    .then(response =>
      dispatch({
        type: Constants.REQUEST_WITHDRAW_POST_SUCCESS,
        payload: response.data,
      }),
    )
    .catch(error =>
      dispatch({
        type: Constants.REQUEST_WITHDRAW_POST_ERROR,
        payload: getValidationErrors(error),
      }),
    );
};
